import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Users, TrendingUp, Shield, Star, Globe, Phone } from "lucide-react";

const VALUE_PROPS = [
  { icon: Users, title: "Serious Buyers, Not Tire Kickers", text: "Every lead that reaches your inbox has been personally qualified by our team. Budget confirmed. Experience verified. Ready to book. No cold leads. No forms filled out at 2 AM." },
  { icon: TrendingUp, title: "Premium Positioning", text: "Your operation is presented alongside the best in the world. Cinematic photography, detailed expedition profiles, and copy that sells without the hard sell. This is how elite outfitters should be represented." },
  { icon: Shield, title: "Vetted Standards", text: "We don't partner with everyone. Our clients trust us because we're selective. If your operation meets our standards, you'll be positioned as part of a small, trusted network — not a marketplace of 500 listings." },
  { icon: Star, title: "Long-Term Partnerships", text: "We don't churn through outfitters. When we find the right fit, we build the relationship over years. Consistent lead flow, honest feedback, and a partner who understands your business." },
  { icon: Globe, title: "Global Reach", text: "Our clients hunt everywhere — Alaska to Africa, Montana to Mongolia. If your operation delivers, we'll send you hunters from across the country and beyond." },
];

const STANDARDS = [
  "Consistent, verifiable track record of client satisfaction",
  "Professional guides with documented field experience",
  "Clear, honest communication with clients and brokers",
  "Safe, well-maintained camps, equipment, and vehicles",
  "Ethical hunting practices and conservation commitment",
  "Transparent pricing — no hidden fees or surprise charges",
  "Reliable post-hunt support (trophy prep, taxidermy, shipping)",
];

export default function Outfitters() {
  return (
    <>
      <Helmet>
        <title>For Outfitters | Dispatch Hunts</title>
        <meta name="description" content="Partner with Dispatch Hunts to reach qualified, high-budget hunters. Vetted standards. Premium positioning. Serious buyers." />
      </Helmet>

      {/* Hero */}
      <section className="section-dark grain pt-28 pb-16" data-testid="outfitters-hero">
        <div className="container-dh text-center max-w-3xl mx-auto">
          <p className="text-label text-brass mb-4">For Outfitters</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone mb-6" data-testid="outfitters-headline">
            We send you hunters who are ready to book.
          </h1>
          <p className="font-body text-bone/60 text-lg max-w-2xl mx-auto">
            Not leads. Not lookers. Qualified, serious buyers with budget in hand and a trip in mind. Here's how we're different from every other broker and lead-gen platform.
          </p>
        </div>
      </section>

      {/* Value Props */}
      <section className="section-chalk py-20 lg:py-28" data-testid="outfitters-value">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">Why Partner With Us</p>
          <h2 className="font-display text-3xl font-bold text-carbon mb-14">Not just another broker.</h2>
          <div className="space-y-10">
            {VALUE_PROPS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex gap-6 max-w-3xl" data-testid={`value-${title.toLowerCase().replace(/[\s,]+/g, "-")}`}>
                <div className="flex-shrink-0 w-12 h-12 bg-brass/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-brass" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-carbon mb-2">{title}</h3>
                  <p className="font-body text-slate leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="outfitters-standards">
        <div className="container-dh max-w-3xl">
          <p className="text-label text-brass mb-3">Our Standards</p>
          <h2 className="font-display text-3xl font-bold text-bone mb-6">What we look for.</h2>
          <p className="font-body text-bone/50 mb-10">We're selective because our clients trust us to be. Here's what it takes to join the Dispatch Hunts network.</p>
          <ul className="space-y-4">
            {STANDARDS.map((s) => (
              <li key={s} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-2 h-2 bg-brass mt-2" />
                <p className="font-body text-bone/70">{s}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-light py-20 lg:py-28" data-testid="outfitters-how">
        <div className="container-dh max-w-3xl text-center">
          <p className="text-label text-brass mb-3">Getting Started</p>
          <h2 className="font-display text-3xl font-bold text-carbon mb-12">Simple process.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-left">
            <div className="text-center">
              <p className="font-mono text-4xl font-bold text-brass/20 mb-3">01</p>
              <h4 className="font-display text-base font-bold text-carbon mb-2">Reach Out</h4>
              <p className="text-sm text-smoke font-body">Send us your operation details, areas, species, and pricing structure.</p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl font-bold text-brass/20 mb-3">02</p>
              <h4 className="font-display text-base font-bold text-carbon mb-2">Team Review</h4>
              <p className="text-sm text-smoke font-body">We evaluate your operation, references, and track record. If it's a fit, we schedule a call.</p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl font-bold text-brass/20 mb-3">03</p>
              <h4 className="font-display text-base font-bold text-carbon mb-2">Go Live</h4>
              <p className="text-sm text-smoke font-body">Your expeditions go on the platform. We start matching you with qualified hunters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-20" data-testid="outfitters-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-bone mb-4">Interested in partnering?</h2>
          <p className="font-body text-bone/50 mb-8">Reach out to us directly. We'll review your operation and get back to you within a week.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:matt@dispatchhunts.com" className="btn-brass" data-testid="outfitters-email-btn">
              Contact Us <ArrowRight className="w-4 h-4" />
            </a>
            <a href="tel:4065805366" className="btn-outline"><Phone className="w-4 h-4" /> 406-580-5366</a>
          </div>
        </div>
      </section>
    </>
  );
}
